import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../Assets/Logo-cyqLkda7.jpeg';
import { User, Mail, Lock, Eye, EyeOff, KeyRound, UserPlus } from 'lucide-react';
import { senvraApi } from '../utils/axiosInstance';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Extract referral code from URL ?ref=... (e.g., BA380DAC)
  const initialReferralCode = searchParams.get('ref') || '';

  // Step management
  const [step, setStep] = useState(1); // 1: Email only, 2: Complete registration
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    otp: '',
    referralCode: initialReferralCode  // autofill from URL if present
  });
  
  // Validation errors
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpSentEmail, setOtpSentEmail] = useState('');

  // Resend OTP timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Email validation only
  const validateEmail = (email) => {
    if (!email) {
      return 'Email Address is required';
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      return 'Email Address is invalid';
    }
    return '';
  };

  // Full registration validation (referral code is optional, no validation errors for it)
  const validateFullForm = (values) => {
    let errors = {};
    
    if (!values.name) {
      errors.name = 'Full Name is required';
    } else if (values.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!values.otp) {
      errors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(values.otp)) {
      errors.otp = 'OTP must be 6 digits';
    }
    
    // No validation for referralCode – optional
    
    return errors;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (step === 1 && name === 'email') {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    } else if (step === 2) {
      setErrors(prev => ({ ...prev, [name]: validateFullForm({ ...formData, [name]: value })[name] }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Step 1: Send OTP
  const handleSendOtp = async () => {
    const emailError = validateEmail(formData.email);
    
    if (emailError) {
      setErrors({ email: emailError });
      setTouched({ email: true });
      return;
    }

    setIsSendingOtp(true);
    setServerError('');
    
    try {
      const response = await senvraApi.post('/auth/register/otp', {
        email: formData.email.toLowerCase()
      });
      
      console.log('OTP sent response:', response.data);
      
      setSuccessMessage(`OTP sent to ${formData.email}`);
      setOtpSentEmail(formData.email);
      setStep(2);
      setResendTimer(60);
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Send OTP error:', error);
      
      // 🔥 IMPROVED ERROR HANDLING
      if (error.response) {
        const errorData = error.response.data;
        let errorMessage = '';
        
        // Check for specific error types
        if (errorData?.error === 'User already exists') {
          errorMessage = ' This email is already registered. Please login instead.';
        } else if (errorData?.error === 'Email already exists') {
          errorMessage = ' This email is already registered. Please login instead.';
        } else if (errorData?.message === 'User already exists') {
          errorMessage = ' This email is already registered. Please login instead.';
        } else if (errorData?.detail === 'User already exists') {
          errorMessage = ' This email is already registered. Please login instead.';
        } else if (errorData?.error) {
          errorMessage = ` ${errorData.error}`;
        } else if (errorData?.message) {
          errorMessage = ` ${errorData.message}`;
        } else if (errorData?.detail) {
          errorMessage = ` ${errorData.detail}`;
        } else {
          errorMessage = ' Failed to send OTP. Please try again.';
        }
        
        setServerError(errorMessage);
      } else if (error.request) {
        setServerError(' Network error. Please check your connection.');
      } else {
        setServerError(' An unexpected error occurred. Please try again.');
      }
      
      // Auto clear error after 5 seconds
      setTimeout(() => setServerError(''), 5000);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: Complete Registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateFullForm(formData);
    setErrors(newErrors);
    setTouched({ name: true, password: true, otp: true });
    
    const isValid = Object.keys(newErrors).length === 0;

    if (isValid) {
      setIsSubmitting(true);
      setServerError('');
      
      try {
        const payload = {
          email: formData.email.toLowerCase(),
          name: formData.name,
          password: formData.password,
          otp: formData.otp,
          // Include referral code only if not empty
          ...(formData.referralCode && { referred_by_code: formData.referralCode.trim() })
        };
        
        const response = await senvraApi.post('/auth/register', payload);
        
        console.log('Registration response:', response.data);
        
        setSuccessMessage(`Account created successfully! Redirecting to login...`);
        
        // Clear form
        setFormData({
          email: '',
          name: '',
          password: '',
          otp: '',
          referralCode: initialReferralCode // reset to original referral from URL
        });
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        
      } catch (error) {
        console.error('Registration error:', error);
        
        if (error.response) {
          const errorMessage = error.response.data?.detail || 
                              error.response.data?.message || 
                              'Registration failed. Please try again.';
          setServerError(errorMessage);
        } else if (error.request) {
          setServerError('Network error. Please check your connection.');
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setIsSendingOtp(true);
    setServerError('');
    
    try {
      await senvraApi.post('/auth/register/otp', {
        email: formData.email.toLowerCase()
      });
      
      setSuccessMessage(`OTP resent to ${formData.email}`);
      setResendTimer(60);
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Resend OTP error:', error);
      setServerError('Failed to resend OTP. Please try again.');
      setTimeout(() => setServerError(''), 5000);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Go back to email step – keep referral code from URL if present
  const handleBackToEmail = () => {
    setStep(1);
    setFormData(prev => ({ 
      ...prev, 
      name: '', 
      password: '', 
      otp: '',
      referralCode: initialReferralCode // keep only the URL referral code
    }));
    setErrors({});
    setTouched({});
    setServerError('');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-[75px] md:pt-[70px]">
      <div className="flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-[520px]">
          {/* Header Content */}
          <div className="pt-8 px-6 text-center mb-10">
            <h1 className="text-4xl font-['Libre_Baskerville'] text-[#1e293b] dark:text-white mb-2 tracking-tight">
              {step === 1 ? 'Create an Account' : 'Complete Registration'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {step === 1 ? (
                <>
                  Already have an account?{' '}
                  <button 
                    onClick={() => navigate('/login')}
                    className="text-blue-600 dark:text-blue-400 font-semibold hover:underline transition"
                  >
                    Sign in here
                  </button>
                </>
              ) : (
                <>
                  Registering with{' '}
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {formData.email}
                  </span>
                  {' • '}
                  <button 
                    onClick={handleBackToEmail}
                    className="text-blue-600 dark:text-blue-400 font-semibold hover:underline transition"
                  >
                    Change email
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Success/Error Notifications */}
          {successMessage && (
            <div className="mb-4 bg-emerald-50 text-emerald-700 text-sm py-3 px-4 rounded-xl font-medium border border-emerald-200 flex items-center gap-2">
              <span>✓</span> {successMessage}
            </div>
          )}
          
          {serverError && (
            <div className="mb-4 bg-red-50 text-red-700 text-sm py-3 px-4 rounded-xl font-medium border border-red-200 flex items-center gap-2">
              <span className="text-red-500">⚠️</span> 
              <span>{serverError}</span>
              <button 
                onClick={() => setServerError('')}
                className="ml-auto text-red-400 hover:text-red-600 transition"
              >
                ✕
              </button>
            </div>
          )}
          
          <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden relative">
            
            {/* Step 1: Email only */}
            {step === 1 && (
              <form onSubmit={(e) => { e.preventDefault(); handleSendOtp(); }} className="p-8 pt-6 space-y-6">
                <div>
                  <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail size={20} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-blue-600" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="you@example.com"
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm transition-all focus:outline-none focus:ring-2 ${
                        touched.email && errors.email
                          ? 'border-red-400 focus:ring-red-200 dark:focus:ring-red-800'
                          : 'border-slate-100 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.email}
                    </p>
                  )}
                  <p className="text-slate-400 text-[11px] mt-2">
                    We'll send a verification code to this email address.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSendingOtp}
                  className="w-full mt-4 py-4 rounded-full font-bold text-base bg-gradient-to-r from-[#0091ea] to-[#00b0ff] text-white transition-all hover:opacity-90 hover:shadow-lg hover:shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSendingOtp ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP →'
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400 px-4 leading-relaxed">
                  By creating an account, you agree to our{' '}
                  <span className="underline cursor-pointer hover:text-slate-500 transition">Terms of Service</span>{' '}
                  and{' '}
                  <span className="underline cursor-pointer hover:text-slate-500 transition">Privacy Policy</span>.
                </p>
              </form>
            )}

            {/* Step 2: Name, Password, OTP + Referral Code */}
            {step === 2 && (
              <form onSubmit={handleSubmit} className="p-8 pt-6 space-y-5">
                {/* Name Field */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Full Name
                  </label>
                  <div className="relative group">
                    <User size={20} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-blue-600" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="John Doe"
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm transition-all focus:outline-none focus:ring-2 ${
                        touched.name && errors.name
                          ? 'border-red-400 focus:ring-red-200 dark:focus:ring-red-800'
                          : 'border-slate-100 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {touched.name && errors.name && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative group">
                    <Lock size={20} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-blue-600" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Create a password"
                      className={`w-full pl-12 pr-12 py-3.5 rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm transition-all focus:outline-none focus:ring-2 ${
                        touched.password && errors.password
                          ? 'border-red-400 focus:ring-red-200 dark:focus:ring-red-800'
                          : 'border-slate-100 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-500'
                      }`}
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600 transition"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.password}</p>
                  )}
                  <p className="text-slate-400 text-[11px] mt-2">
                    Minimum 6 characters.
                  </p>
                </div>

                {/* OTP Field with Resend */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Verification Code
                  </label>
                  <div className="relative group">
                    <KeyRound size={20} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-blue-600" />
                    <input
                      type="text"
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm transition-all focus:outline-none focus:ring-2 ${
                        touched.otp && errors.otp
                          ? 'border-red-400 focus:ring-red-200 dark:focus:ring-red-800'
                          : 'border-slate-100 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {touched.otp && errors.otp && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.otp}</p>
                  )}
                  
                  {/* Resend OTP Button */}
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || isSendingOtp}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                    </button>
                  </div>
                </div>

                {/* Referral Code Field (Optional, autofilled from URL) */}
                <div>
                  <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Referral Code <span className="text-slate-400 text-[11px] font-normal">(Optional)</span>
                  </label>
                  <div className="relative group">
                    <UserPlus size={20} className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-400 group-focus-within:text-blue-600" />
                    <input
                      type="text"
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter referral code"
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 text-sm transition-all focus:outline-none focus:ring-2 border-slate-100 dark:border-slate-700 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-400 dark:focus:border-blue-500"
                    />
                  </div>
                  {/* No validation error for referralCode */}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-2 py-4 rounded-full font-bold text-base bg-gradient-to-r from-[#0091ea] to-[#00b0ff] text-white transition-all hover:opacity-90 hover:shadow-lg hover:shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account →'
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400 px-4 leading-relaxed">
                  By creating an account, you agree to our{' '}
                  <span className="underline cursor-pointer hover:text-slate-500 transition">Terms of Service</span>{' '}
                  and{' '}
                  <span className="underline cursor-pointer hover:text-slate-500 transition">Privacy Policy</span>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;